import React, {useEffect} from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import {QuestionGroup, useNeuroMessages, useQuestionContext} from '../../components';
import {useAnswers, useGuideConfig, usePages} from '../../hooks';
import {createStrictContext} from '../../state/_utils/createContext';

/**
 * @typedef SurveyCurrentPageObj
 * @property {Object} answers
 * @property {Object} page
 * @property {Object} path
 * @property {Array} questionGroups

 * @typedef SurveyPageContextObject
 * @property {string} componentClass - page class name
 * @property {SurveyCurrentPageObj} currentSurveyPage - properties of the current survey page
 * @property {function} renderQuestionGroups - renders the question groups of the current survey page
 * @property {Array} validationErrors - list of validation errors on the current survey page
 */

const createdContext = createStrictContext({
  name: 'SurveyPageContext',
});
/** @type {SurveyPageContextObject} */
const SurveyPageContext = createdContext[0];
/** @returns {SurveyPageContextObject} */
const useSurveyPageContext = createdContext[1];

function SurveyPageProvider({ children, Component }) {
  const { answers = {} } = useAnswers();
  const { getMessage } = useNeuroMessages();
  const { isDemoMode } = useGuideConfig();
  const questionComponents = useQuestionContext();
  const {
    currentPageUrl,
    defaultPageTitle,
    goToNextPage,
    goToPreviousPage,
    savedPages,
    isLoadingNextPage = false,
    validationErrors = []
  } = usePages();

  const currentPage = isDemoMode ? savedPages[currentPageUrl] : savedPages[location.pathname];
  const currentSurveyPage = {
    answers,
    page: currentPage?.page,
    path: currentPage?.path,
    questionGroups: currentPage?.questionGroups,
  };

  /**
   * Change the document title if a message exists based on the survey path / page keys
   * TODO: document feature
   */
  useEffect(() => {
    const surveyPathTitleMsg = getMessage(`surveyPage.meta.title.${currentPage?.path?.key}`);
    const surveyPageTitleMsg = getMessage(`surveyPage.meta.title.${currentPage?.path?.key}.${currentPage?.page?.key}`);
    if( surveyPageTitleMsg || surveyPathTitleMsg ) {
      document.title = surveyPageTitleMsg || surveyPathTitleMsg;
    } else if(document.title !== defaultPageTitle) {
      document.title = defaultPageTitle;
    }
  }, [currentPageUrl]);

  const renderQuestionGroups = React.useCallback(
    () =>
      (currentSurveyPage.questionGroups || []).map((questionGroup, i) => {
        return (
          <QuestionGroup
            GroupComponent={questionComponents.getQuestionGroupComponent(
              questionGroup.type
            )}
            key={`${questionGroup.key}-${i}`}
            page={currentSurveyPage}
            validationErrors={validationErrors}
            questionGroup={questionGroup}
          />
        );
      }),
    [location, validationErrors, answers]
  );

  const componentClass = 'surveyPage';
  const componentClassNames = classnames(componentClass, {
    [`surveyPage--path-${currentPage?.path?.key}`]: currentPage,
    [`surveyPage--page-${currentPage?.page?.key}`]: currentPage,
  });

  /** @type SurveyPageContextObject */
  const contextValue = {
    componentClass,
    currentSurveyPage,
    renderQuestionGroups,
    validationErrors
  };
  return (
    <SurveyPageContext.Provider value={contextValue}>
      {Component
        ? (<Component
          className={componentClassNames}
          isLoadingNextPage={isLoadingNextPage}
          goToNextPage={goToNextPage}
          goToPreviousPage={goToPreviousPage}
          {...contextValue}
        />) : children
      }
    </SurveyPageContext.Provider>
  );
}

SurveyPageProvider.propTypes = {
  children: PropTypes.any,
  Component: PropTypes.oneOfType([PropTypes.element, PropTypes.func])
    .isRequired,
};

export { useSurveyPageContext };
export default SurveyPageProvider;
